import { useMemo } from "react";
import { CalendarDays, GitBranch, Info, Layers } from "lucide-react";
import { Badge } from "../../../../../components/tailwind/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../../../../../components/tailwind/card";
import { Label } from "../../../../../components/tailwind/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "../../../../../components/tailwind/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "../../../../../components/tailwind/tooltip";
import {
  buildAnalyticsMonthOptions,
  formatAnalyticsMonthLabel
} from "../../../utils/workflowAnalyticsUtils";
import { workflowTheme } from "../../../utils/workflowBuilderTheme";

const STATUS_BADGE_CLASS = {
  LIVE: "border-none bg-green-100 text-green-700 dark:bg-green-950/40 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-950/40",
  ACTIVE: "border-none bg-green-100 text-green-700 dark:bg-green-950/40 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-950/40",
  DRAFT: "border-none bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-950/40",
  ARCHIVED: "border-none bg-gray-100 text-gray-600 dark:bg-slate-800 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-800",
  PAUSED: "border-none bg-orange-100 text-orange-700 dark:bg-orange-950/40 dark:text-orange-400 hover:bg-orange-100 dark:hover:bg-orange-950/40"
};

const VERSION_STATUS_BADGE_LABEL = {
  ACTIVE: "LIVE",
  LIVE: "LIVE",
  DRAFT: "DRAFT",
  ARCHIVED: "ARCHIVED",
  PAUSED: "PAUSED"
};

const VERSION_STATUS_GLOSSARY = [
  {
    status: "LIVE",
    label: "Live",
    textClass: "text-green-700 dark:text-green-400",
    description:
      "Workflows that are currently active and can be invoked by end users based on the defined intents."
  },
  {
    status: "ARCHIVED",
    label: "Archived",
    textClass: "text-gray-600 dark:text-gray-400",
    description:
      "Workflows go into this state after a new version of the same workflow has been published; these workflows cannot be invoked by end users."
  }
];

const TRIGGER_CLASS = `h-11 text-sm rounded-lg border shadow-sm ${workflowTheme.input} focus:ring-2 focus:ring-[#0F6E56]/30 dark:focus:ring-emerald-500/30`;

function FilterField({ icon: Icon, label, hint, children }) {
  return (
    <div className="space-y-1.5 min-w-0">
      <Label className={`flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide ${workflowTheme.textSecondary}`}>
        <Icon className="h-3.5 w-3.5 shrink-0" />
        {label}
      </Label>
      {children}
      {hint && <p className={`text-[11px] ${workflowTheme.textMuted}`}>{hint}</p>}
    </div>
  );
}

function StatusBadge({ status }) {
  if (!status) return null;
  const label = VERSION_STATUS_BADGE_LABEL[status] || status;
  return (
    <Badge
      variant="secondary"
      className={`shrink-0 text-[10px] font-bold uppercase tracking-wide px-1.5 py-0 ${STATUS_BADGE_CLASS[status] || STATUS_BADGE_CLASS.DRAFT}`}
    >
      {label}
    </Badge>
  );
}

/** `getWorkflowVersionSelectOptions` labels look like `v2 (ACTIVE)`. */
function parseVersionOption(opt) {
  const statusMatch = /\(([^)]+)\)/.exec(opt.label);
  return {
    versionLabel: `Version ${opt.value}`,
    status: statusMatch?.[1] || null
  };
}

function VersionOptionLabel({ opt }) {
  const { versionLabel, status } = parseVersionOption(opt);
  return (
    <span className="flex items-center gap-2 min-w-0">
      <span className="truncate font-medium">{versionLabel}</span>
      <StatusBadge status={status} />
    </span>
  );
}

function VersionStatusGlossary() {
  return (
    <TooltipProvider delayDuration={200}>
      <div className="flex items-center gap-4 sm:gap-5" aria-label="Version status meanings">
        {VERSION_STATUS_GLOSSARY.map(({ status, label, textClass, description }) => (
          <Tooltip key={status}>
            <TooltipTrigger asChild>
              <button
                type="button"
                className={`inline-flex items-center gap-1.5 text-base sm:text-lg font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0F6E56]/40 rounded-sm ${textClass}`}
              >
                <Info className="h-4 w-4 sm:h-[18px] sm:w-[18px] shrink-0 opacity-80" aria-hidden />
                {label}
              </button>
            </TooltipTrigger>
            <TooltipContent
              side="bottom"
              className="max-w-xs bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900"
            >
              {description}
            </TooltipContent>
          </Tooltip>
        ))}
      </div>
    </TooltipProvider>
  );
}

export function WorkflowAnalyticsFilters({
  workflows,
  workflowId,
  onWorkflowIdChange,
  version,
  onVersionChange,
  versionOptions,
  month,
  onMonthChange,
  listLoading
}) {
  const monthOptions = useMemo(() => {
    const options = buildAnalyticsMonthOptions(12);
    if (month && !options.some((opt) => opt.value === month)) {
      options.push({
        value: month,
        label: formatAnalyticsMonthLabel(month),
        isCurrent: false
      });
      options.sort((a, b) => b.value.localeCompare(a.value));
    }
    return options;
  }, [month]);

  const selectedWorkflow = workflows.find((item) => item.workflowId === workflowId);
  const selectedVersion = versionOptions.find((opt) => opt.value === version);
  const selectedMonth = monthOptions.find((opt) => opt.value === month);

  return (
    <Card className={`shadow-sm ${workflowTheme.card}`}>
      <CardHeader className="p-4 md:p-5 pb-2 space-y-1">
        <div className="flex items-center justify-between gap-3">
          <CardTitle className={`text-sm ${workflowTheme.textPrimary}`}>Filters</CardTitle>
          <VersionStatusGlossary />
        </div>
        <p className={`text-xs font-normal ${workflowTheme.textSecondary}`}>
          Choose a workflow, version, and month to explore analytics
        </p>
      </CardHeader>
      <CardContent className="p-4 md:p-5 pt-3">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <FilterField
            icon={GitBranch}
            label="Workflow"
            hint={listLoading ? "Loading workflows…" : undefined}
          >
            <Select
              value={workflowId || undefined}
              onValueChange={onWorkflowIdChange}
              disabled={listLoading || !workflows.length}
            >
              <SelectTrigger className={TRIGGER_CLASS}>
                <SelectValue placeholder="Select a workflow">
                  {selectedWorkflow
                    ? selectedWorkflow.name || "Untitled workflow"
                    : undefined}
                </SelectValue>
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {workflows.map((workflow) => (
                  <SelectItem key={workflow.workflowId} value={workflow.workflowId} className="py-2.5">
                    <span className="truncate font-medium">
                      {workflow.name || "Untitled workflow"}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FilterField>

          <FilterField
            icon={Layers}
            label="Version"
            hint={
              versionOptions.length
                ? `${versionOptions.length} version${versionOptions.length === 1 ? "" : "s"} available`
                : "No versions for this workflow"
            }
          >
            <Select
              value={version || undefined}
              onValueChange={onVersionChange}
              disabled={!versionOptions.length}
            >
              <SelectTrigger className={TRIGGER_CLASS}>
                <SelectValue placeholder="Select version">
                  {selectedVersion ? <VersionOptionLabel opt={selectedVersion} /> : undefined}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {versionOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value} className="py-2.5">
                    <VersionOptionLabel opt={opt} />
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FilterField>

          <FilterField
            icon={CalendarDays}
            label="Month"
            hint="Shows totals for this calendar month"
          >
            <Select value={month || undefined} onValueChange={onMonthChange}>
              <SelectTrigger className={TRIGGER_CLASS}>
                <SelectValue placeholder="Select month">
                  {selectedMonth?.label}
                </SelectValue>
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {monthOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value} className="py-2.5">
                    <span className="flex items-center justify-between gap-3 w-full pr-4">
                      <span>{opt.label}</span>
                      {opt.isCurrent && (
                        <Badge
                          variant="secondary"
                          className="border-none text-[10px] font-bold uppercase tracking-wide px-1.5 py-0 bg-[#E1F5EE] text-[#085041] dark:bg-emerald-950/40 dark:text-emerald-300 hover:bg-[#E1F5EE] dark:hover:bg-emerald-950/40"
                        >
                          Current
                        </Badge>
                      )}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FilterField>
        </div>
      </CardContent>
    </Card>
  );
}
